/**
 * RedirectToProject
 * Handles legacy URL redirects to project-scoped URLs
 * Now workspace-aware — builds /w/:wsSlug/:prefix/:shortId/:path URLs
 */

import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useProject, getProjectShortId } from '../../contexts/ProjectContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';

interface RedirectToProjectProps {
  path: string;
  noProjectRedirect?: string;
  prefix?: string;
}

export function RedirectToProject({
  path,
  noProjectRedirect = '/projects',
  prefix = 'product',
}: RedirectToProjectProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { shortId, isLoading, allProjects } = useProject();
  const { wsSlug } = useWorkspace();

  useEffect(() => {
    if (isLoading) return;

    // Use current project shortId, or fall back to first project in list
    const targetShortId = shortId || (allProjects.length > 0 ? getProjectShortId(allProjects[0].id) : null);

    if (targetShortId) {
      const suffix = path ? `/${path}` : '';
      const targetPath = wsSlug
        ? `/w/${wsSlug}/${prefix}/${targetShortId}${suffix}`
        : `/${prefix}/${targetShortId}${suffix}`;
      // Preserve query params (e.g. ?topic=xxx) so topic focus survives the redirect
      navigate(`${targetPath}${location.search}`, { replace: true });
    } else if (allProjects.length === 0) {
      navigate(noProjectRedirect, { replace: true });
    }
  }, [shortId, isLoading, allProjects, path, prefix, navigate, noProjectRedirect, wsSlug, location.search]);

  return (
    <div className="flex items-center justify-center h-full">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-coral-500 mx-auto mb-2"></div>
        <p className="text-sm text-neutral-500">Redirecting...</p>
      </div>
    </div>
  );
}

export default RedirectToProject;
